/**
 * Video Debug Page - Real LiveKit/OpenVidu Connection Test
 * Tests actual video connectivity without authentication (development only)
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  useVideoRoom,
  useMediaControls,
  useBackgroundProcessor,
  useVideoBackgrounds,
  ConnectionStatus,
  ControlBar,
  VideoLoadingState,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  TooltipProvider,
} from "@jourfx/ui";
import { User, AlertCircle, CheckCircle2, Copy, MessageSquare, UserPlus, Settings } from "lucide-react";
import { VideoSidebar } from "../components/video/VideoSidebar";
import { InviteModal } from "../components/video/InviteModal";
import { SettingsPanel } from "../components/video/SettingsPanel";
import { Track } from "livekit-client";

const VIDEO_DEBUG_ENABLED = import.meta.env.VITE_ENABLE_VIDEO_DEBUG === "true";

export const Route = createFileRoute("/video-debug")({
  component: VideoDebugRoute,
});

type DebugState = "lobby" | "getting-token" | "connecting" | "connected" | "demo" | "error";

interface TokenInfo {
  token: string;
  serverUrl: string;
  room: string;
  identity: string;
  signalPath?: "v0";
}

export function VideoDebugRoute() {
  if (!VIDEO_DEBUG_ENABLED) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-xl border bg-white dark:bg-stone-900 p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold">Video-Debug deaktiviert</h1>
          <p className="text-sm text-muted-foreground">
            Diese Seite ist nur fuer Entwicklungs- und Testumgebungen gedacht.
          </p>
          <p className="text-xs text-muted-foreground">
            Setzen Sie <code className="font-mono">VITE_ENABLE_VIDEO_DEBUG=true</code> um sie bewusst zu aktivieren.
          </p>
        </div>
      </div>
    );
  }

  return <VideoDebugPage />;
}

function VideoDebugPage() {
  const [state, setState] = useState<DebugState>("lobby");
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [userName, setUserName] = useState(`Debug-User-${Math.floor(Math.random() * 1000)}`);
  const [roomName, setRoomName] = useState(`debug-room-${Date.now()}`);
  const [e2eeEnabled, setE2eeEnabled] = useState(true); // Toggle for testing

  // Selected devices from preview (passed to OpenVidu)
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");

  // Sidebar and modal state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Convex action for debug token
  const generateDebugToken = useAction(api.video.debugToken.generate);

  // E2EE shared key - random per session for debug (production uses server-generated keys)
  const [e2eeSharedKey] = useState(() => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
  });

  // Video room hook with E2EE (conditionally enabled for testing)
  const {
    room,
    connectionState,
    localParticipant,
    remoteParticipants,
    isE2EEEnabled,
    connect,
    disconnect,
    error: roomError,
  } = useVideoRoom({
    serverUrl: tokenInfo?.serverUrl || "",
    token: tokenInfo?.token || "",
    role: "gutachter",
    forceLegacySignalPath: tokenInfo?.signalPath ? tokenInfo.signalPath === "v0" : true,
    e2ee: e2eeEnabled ? {
      sharedKey: e2eeSharedKey,
    } : undefined,
  });

  // Media controls
  const {
    isMicEnabled,
    isCameraEnabled,
    isScreenSharing,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    microphoneError,
    cameraError,
  } = useMediaControls({ room });

  // Background effects
  const adminBackgrounds = useQuery(api.admin.videoBackgrounds.listPublic);
  const presets = useVideoBackgrounds(adminBackgrounds);
  const background = useBackgroundProcessor({
    room,
    presets,
  });

  // Auto-enable camera and mic after connection with selected devices
  useEffect(() => {
    if (connectionState === "connected" && room?.localParticipant) {
      // Use selected devices from preview if available
      const cameraOptions = selectedVideoDevice
        ? { deviceId: selectedVideoDevice }
        : undefined;
      const micOptions = selectedAudioDevice
        ? { deviceId: selectedAudioDevice }
        : undefined;

      room.localParticipant.setCameraEnabled(true, cameraOptions).catch(console.error);
      room.localParticipant.setMicrophoneEnabled(true, micOptions).catch(console.error);
    }
  }, [connectionState, room, selectedVideoDevice, selectedAudioDevice]);

  // Handle connection state changes
  useEffect(() => {
    if (connectionState === "connected" && state === "connecting") {
      setState("connected");
      setSessionStartTime(Date.now());
    }
  }, [connectionState, state]);

  // Handle room errors
  useEffect(() => {
    if (roomError) {
      setErrorMessage(roomError.message);
      setState("error");
    }
  }, [roomError]);

  // Start connection process
  const handleStartConnection = async () => {
    setState("getting-token");
    setErrorMessage("");

    try {
      // Generate debug token
      const result = await generateDebugToken({
        name: userName,
        room: roomName,
      });

      setTokenInfo(result);
      setState("connecting");

      // Small delay then connect
      setTimeout(async () => {
        try {
          // The useVideoRoom hook will handle connection when token is set
        } catch (err) {
          setErrorMessage(err instanceof Error ? err.message : "Connection failed");
          setState("error");
        }
      }, 100);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to get token");
      setState("error");
    }
  };

  // Connect when token info is available
  useEffect(() => {
    if (tokenInfo && state === "connecting" && connectionState === "disconnected") {
      connect().catch((err) => {
        setErrorMessage(err instanceof Error ? err.message : "Connection failed");
        setState("error");
      });
    }
  }, [tokenInfo, state, connectionState, connect]);

  // Handle leave
  const handleLeave = () => {
    disconnect();
    setState("lobby");
    setTokenInfo(null);
    setSessionStartTime(null);
  };

  // Copy room link for second participant
  const copyJoinLink = () => {
    const url = `${window.location.origin}/video-debug?room=${roomName}&e2ee=${e2eeEnabled ? '1' : '0'}`;
    navigator.clipboard.writeText(url);
  };

  // Check for room param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get("room");
    const e2eeParam = params.get("e2ee");
    if (roomParam) {
      setRoomName(roomParam);
    }
    if (e2eeParam !== null) {
      setE2eeEnabled(e2eeParam === '1');
    }
  }, []);

  // ========== LOBBY STATE ==========
  if (state === "lobby") {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <CardTitle>Video Debug - OpenVidu Test</CardTitle>
            <CardDescription>
              Test LiveKit/OpenVidu connectivity without authentication
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Camera Preview */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Equipment Check</p>
              <LocalCameraPreview
                userName={userName}
                selectedVideoDevice={selectedVideoDevice}
                selectedAudioDevice={selectedAudioDevice}
                onVideoDeviceChange={setSelectedVideoDevice}
                onAudioDeviceChange={setSelectedAudioDevice}
              />
            </div>

            {/* Connection info */}
            <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Server:</span>
                <code className="text-sage-600">ws://localhost:7880</code>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">API Key:</span>
                <code className="text-sage-600">devkey</code>
              </div>
            </div>

            {/* User settings */}
            <div className="space-y-4">
              <div>
                <label htmlFor="video-debug-user-name" className="block text-sm font-medium mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  id="video-debug-user-name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                />
              </div>
              <div>
                <label htmlFor="video-debug-room-name" className="block text-sm font-medium mb-1">
                  Room Name
                </label>
                <input
                  type="text"
                  id="video-debug-room-name"
                  value={roomName}
                  onChange={(e) => setRoomName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="e2ee-toggle"
                  checked={e2eeEnabled}
                  onChange={(e) => setE2eeEnabled(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="e2ee-toggle" className="text-sm">
                  Enable E2E Encryption
                  <span className="text-muted-foreground text-xs ml-2">
                    (disable if video doesn't work)
                  </span>
                </label>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <Button
                className="w-full"
                size="lg"
                onClick={handleStartConnection}
                data-testid="connect-btn"
              >
                Connect to OpenVidu
              </Button>
              <Button
                className="w-full"
                size="lg"
                variant="outline"
                onClick={() => {
                  setState("demo");
                  setSessionStartTime(Date.now());
                }}
                data-testid="demo-mode-btn"
              >
                Demo Mode (UI Only)
              </Button>
            </div>

            {/* Back link */}
            <div className="pt-4 border-t">
              <Link to="/login" search={{ redirect: "/video-debug" }} className="text-sm text-primary hover:underline">
                &larr; Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== ERROR STATE ==========
  if (state === "error") {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Connection Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{errorMessage}</p>
            </div>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="font-medium">Troubleshooting:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Verify Docker containers are running: <code>docker ps</code></li>
                <li>Check OpenVidu is on port 7880</li>
                <li>Verify Convex env vars: LIVEKIT_API_KEY, LIVEKIT_API_SECRET, LIVEKIT_URL</li>
                <li>Check browser console for detailed errors</li>
              </ul>
            </div>

            <Button
              className="w-full"
              variant="outline"
              onClick={() => setState("lobby")}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== GETTING TOKEN STATE ==========
  if (state === "getting-token") {
    return (
      <VideoLoadingState
        message="Generating access token..."
      />
    );
  }

  // ========== CONNECTING STATE ==========
  if (state === "connecting") {
    return (
      <VideoLoadingState
        message="Connecting to OpenVidu..."
        showDeviceCheck
      />
    );
  }

  // ========== DEMO STATE (UI Only, no real connection) ==========
  if (state === "demo") {
    const mockAppointmentId = "debug-appointment-123";

    return (
      <TooltipProvider>
        <div className="h-screen bg-stone-950 flex" data-testid="demo-video-room">
          {/* Main content area */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <header className="flex items-center justify-between p-4 bg-card/80 backdrop-blur border-b">
              <div className="flex items-center gap-3" data-testid="demo-connection-status">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="text-sm font-medium">Demo Mode</span>
                </div>
                <span className="text-xs text-muted-foreground">No real connection</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">User:</span>
                <code className="text-sage-600" data-testid="demo-user-name">{userName}</code>
              </div>
            </header>

            {/* Video area with spotlight layout */}
            <div className="flex-1 relative">
              {/* Spotlight: Versicherter (remote participant) - full area */}
              <div className="absolute inset-0 bg-stone-900" data-testid="demo-spotlight-video">
                <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-primary flex items-center justify-center mx-auto mb-4">
                      <span className="text-5xl font-semibold text-white">M</span>
                    </div>
                    <p className="text-white text-xl font-medium" data-testid="demo-spotlight-name">Max Mustermann</p>
                    <p className="text-stone-400 text-sm mt-1">Versicherter</p>
                  </div>
                </div>
                {/* Name overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                  <span className="text-white font-medium">Max Mustermann</span>
                </div>
              </div>

              {/* Local participant - small picture-in-picture bottom-left */}
              <div
                className="absolute bottom-4 left-4 w-48 h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700 z-10"
                data-testid="demo-local-video"
              >
                <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-sage-600 flex items-center justify-center mx-auto mb-2">
                      <span className="text-lg font-semibold text-white">
                        {userName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-white text-sm font-medium" data-testid="demo-local-name">{userName}</p>
                  </div>
                </div>
                {/* Name overlay */}
                <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent">
                  <span className="text-white text-xs">Sie</span>
                </div>
              </div>

              {/* Sidebar toggle when closed */}
              {!sidebarOpen && (
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute top-4 right-4 h-12 w-12 rounded-full shadow-lg"
                  onClick={() => setSidebarOpen(true)}
                  data-testid="sidebar-toggle-floating"
                >
                  <MessageSquare className="h-5 w-5" />
                </Button>
              )}
            </div>

            {/* Demo control bar */}
            <div className="px-4 py-3 bg-card/80 backdrop-blur border-t flex items-center justify-center gap-3">
              {/* Mock media controls */}
              <Button
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full"
                data-testid="demo-mic-btn"
                aria-label="Mikrofon (Demo)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full"
                data-testid="demo-camera-btn"
                aria-label="Kamera (Demo)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full"
                data-testid="demo-screen-btn"
                aria-label="Bildschirm teilen (Demo)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>
              </Button>

              {/* Divider */}
              <div className="w-px h-8 bg-border mx-2" />

              {/* Sidebar toggle */}
              <Button
                variant={sidebarOpen ? "default" : "secondary"}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle-btn"
                aria-label={sidebarOpen ? "Sidebar schließen" : "Sidebar öffnen"}
              >
                <MessageSquare className="h-5 w-5" />
              </Button>

              {/* Invite button */}
              <Button
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => setShowInvite(true)}
                data-testid="invite-btn"
                aria-label="Person einladen"
              >
                <UserPlus className="h-5 w-5" />
              </Button>

              {/* Settings button */}
              <Button
                variant="secondary"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => setShowSettings(true)}
                data-testid="settings-btn"
                aria-label="Einstellungen"
              >
                <Settings className="h-5 w-5" />
              </Button>

              {/* Divider */}
              <div className="w-px h-8 bg-border mx-2" />

              {/* Leave button */}
              <Button
                variant="destructive"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => {
                  setState("lobby");
                  setSessionStartTime(null);
                }}
                data-testid="demo-leave-btn"
                aria-label="Demo beenden"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" x2="12" y1="2" y2="12"/></svg>
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <VideoSidebar
            appointmentId={mockAppointmentId}
            room={null}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            isDemo
          />

          {/* Modals */}
          {showInvite && (
            <InviteModal
              appointmentId={mockAppointmentId}
              onClose={() => setShowInvite(false)}
            />
          )}
          {showSettings && (
            <SettingsPanel
              room={null}
              background={background}
              presets={presets}
              isMirrored={true}
              onMirrorChange={() => {}}
              onClose={() => setShowSettings(false)}
            />
          )}
        </div>
      </TooltipProvider>
    );
  }

  // ========== CONNECTED STATE ==========
  // Mock appointment ID for sidebar components
  const mockAppointmentId = "debug-appointment-123";

  return (
    <TooltipProvider>
      <div className="h-screen bg-stone-950 flex">
        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="flex items-center justify-between p-4 bg-card/80 backdrop-blur border-b">
            <ConnectionStatus
              status={connectionState}
              startTime={sessionStartTime || undefined}
              isE2EEEnabled={isE2EEEnabled}
            />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-muted-foreground">Room:</span>
                <code className="text-sage-600">{tokenInfo?.room}</code>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyJoinLink}
                className="gap-2"
              >
                <Copy className="h-3 w-3" />
                Copy Join Link
              </Button>
            </div>
          </header>

          {/* Video area with spotlight layout */}
          <div className="flex-1 relative">
            {/* Spotlight: Remote participant (Versicherter) - full area */}
            <div className="absolute inset-0 bg-stone-900">
              {remoteParticipants.length > 0 ? (
                remoteParticipants.map((participant) => (
                  <RemoteVideoTile
                    key={participant.sid}
                    participant={participant}
                    spotlight
                  />
                ))
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
                  <div className="text-center p-8">
                    <User className="h-16 w-16 text-stone-600 mx-auto mb-4" />
                    <p className="text-stone-400">Waiting for other participants...</p>
                    <p className="text-stone-500 text-sm mt-2">
                      Open another browser tab and join with the same room name
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Local participant - small picture-in-picture bottom-left */}
            <div className="absolute bottom-4 left-4 w-48 h-36 bg-stone-900 rounded-xl overflow-hidden shadow-lg border border-stone-700 z-10">
              <LocalVideoTile
                participant={localParticipant}
                name={userName}
                isMuted={!isMicEnabled}
                isCameraOff={!isCameraEnabled}
                pip
              />
            </div>

            {/* Sidebar toggle when closed */}
            {!sidebarOpen && (
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-4 right-4 h-12 w-12 rounded-full shadow-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="sidebar-toggle-floating"
              >
                <MessageSquare className="h-5 w-5" />
              </Button>
            )}
          </div>

          {/* Debug info panel */}
          <div className="px-4 pb-2">
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer hover:text-foreground">Debug Info</summary>
              <div className="mt-2 p-3 bg-muted rounded-lg grid grid-cols-2 gap-2">
                <div>Identity: <code>{tokenInfo?.identity}</code></div>
                <div>Room: <code>{tokenInfo?.room}</code></div>
                <div>Server: <code>{tokenInfo?.serverUrl}</code></div>
                <div>Connection: <code>{connectionState}</code></div>
                <div>Local tracks: <code>{localParticipant?.trackPublications?.size || 0}</code></div>
                <div>Remote participants: <code>{remoteParticipants.length}</code></div>
                <div className="col-span-2">
                  E2E Encryption: <code className={isE2EEEnabled ? "text-green-600" : "text-amber-600"}>
                    {isE2EEEnabled ? "ENABLED" : "DISABLED"}
                  </code>
                </div>
                {microphoneError && <div className="col-span-2 text-destructive">Mic error: {microphoneError.message}</div>}
                {cameraError && <div className="col-span-2 text-destructive">Camera error: {cameraError.message}</div>}
              </div>
            </details>
          </div>

          {/* Control bar with extended controls */}
          <ControlBar
            isMicEnabled={isMicEnabled}
            isCameraEnabled={isCameraEnabled}
            isScreenSharing={isScreenSharing}
            onToggleMic={toggleMic}
            onToggleCamera={toggleCamera}
            onToggleScreenShare={toggleScreenShare}
            onLeave={handleLeave}
            leaveLabel="Disconnect"
          >
            {/* Sidebar toggle */}
            <Button
              variant={sidebarOpen ? "default" : "secondary"}
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle-btn"
              aria-label={sidebarOpen ? "Sidebar schließen" : "Sidebar öffnen"}
            >
              <MessageSquare className="h-5 w-5" />
            </Button>

            {/* Invite button */}
            <Button
              variant="secondary"
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={() => setShowInvite(true)}
              data-testid="invite-btn"
              aria-label="Person einladen"
            >
              <UserPlus className="h-5 w-5" />
            </Button>

            {/* Settings button */}
            <Button
              variant="secondary"
              size="icon"
              className="h-12 w-12 rounded-full"
              onClick={() => setShowSettings(true)}
              data-testid="settings-btn"
              aria-label="Einstellungen"
            >
              <Settings className="h-5 w-5" />
            </Button>
          </ControlBar>
        </div>

        {/* Sidebar */}
        <VideoSidebar
          appointmentId={mockAppointmentId}
          room={room}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Modals */}
        {showInvite && (
          <InviteModal
            appointmentId={mockAppointmentId}
            onClose={() => setShowInvite(false)}
          />
        )}
        {showSettings && (
          <SettingsPanel
            room={room}
            background={background}
            presets={presets}
            isMirrored={true}
            onMirrorChange={() => {}}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

// ========== Video Tile Components ==========

// Local camera preview for lobby (uses getUserMedia directly, no LiveKit)
interface LocalCameraPreviewProps {
  userName: string;
  selectedVideoDevice: string;
  selectedAudioDevice: string;
  onVideoDeviceChange: (deviceId: string) => void;
  onAudioDeviceChange: (deviceId: string) => void;
}

function LocalCameraPreview({
  userName,
  selectedVideoDevice,
  selectedAudioDevice,
  onVideoDeviceChange,
  onAudioDeviceChange,
}: LocalCameraPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [status, setStatus] = useState<"idle" | "requesting" | "active" | "error">("idle");
  const [error, setError] = useState<string>("");
  const [audioLevel, setAudioLevel] = useState(0);

  // Device selection (local state for the list, parent controls selected)
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);

  // Load available devices
  const loadDevices = async () => {
    try {
      // Need to request permission first to get device labels
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === "videoinput");
      const audioInputs = devices.filter(d => d.kind === "audioinput");
      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);
      // Set defaults if not already set (notify parent)
      if (!selectedVideoDevice && videoInputs.length > 0) {
        onVideoDeviceChange(videoInputs[0].deviceId);
      }
      if (!selectedAudioDevice && audioInputs.length > 0) {
        onAudioDeviceChange(audioInputs[0].deviceId);
      }
      return { videoInputs, audioInputs };
    } catch (err) {
      console.error("Failed to enumerate devices:", err);
      return { videoInputs: [], audioInputs: [] };
    }
  };

  const startPreview = async (videoDeviceId?: string, audioDeviceId?: string) => {
    // Stop existing preview first
    stopPreviewInternal();

    setStatus("requesting");
    setError("");
    try {
      const constraints: MediaStreamConstraints = {
        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Update selected devices from actual stream (notify parent)
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        if (settings.deviceId) onVideoDeviceChange(settings.deviceId);
      }
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        if (settings.deviceId) onAudioDeviceChange(settings.deviceId);
      }

      // Reload device list to get labels (requires permission)
      await loadDevices();

      // Set up audio level monitoring
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Start monitoring audio levels
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          // Calculate average volume
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          // Normalize to 0-100
          setAudioLevel(Math.min(100, Math.round((average / 128) * 100)));
          animationFrameRef.current = requestAnimationFrame(updateLevel);
        }
      };
      updateLevel();

      setStatus("active");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to access camera");
      setStatus("error");
    }
  };

  // Handle device change (notify parent and restart preview)
  const handleVideoDeviceChange = (deviceId: string) => {
    onVideoDeviceChange(deviceId);
    if (status === "active") {
      startPreview(deviceId, selectedAudioDevice);
    }
  };

  const handleAudioDeviceChange = (deviceId: string) => {
    onAudioDeviceChange(deviceId);
    if (status === "active") {
      startPreview(selectedVideoDevice, deviceId);
    }
  };

  // Internal stop without resetting status (for device switching)
  const stopPreviewInternal = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setAudioLevel(0);
  };

  const stopPreview = () => {
    stopPreviewInternal();
    setStatus("idle");
  };

  // Auto-start preview on mount
  useEffect(() => {
    startPreview(undefined, undefined);
  }, []);

  // Listen for device changes (e.g., iPhone Continuity Camera connecting)
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log("Device change detected, refreshing device list...");
      loadDevices();
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="relative aspect-video bg-stone-800 rounded-lg overflow-hidden">
        {/* Always render video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover ${status !== "active" ? "hidden" : ""}`}
        />

        {/* Idle/Requesting state - show loading */}
        {(status === "idle" || status === "requesting") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-stone-700 flex items-center justify-center animate-pulse">
              <span className="text-2xl font-semibold text-white">
                {userName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="text-stone-400 text-sm">Starting camera...</div>
          </div>
        )}

        {/* Error state */}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button onClick={() => startPreview(selectedVideoDevice || undefined, selectedAudioDevice || undefined)} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        )}

        {/* Active - show stop button */}
        {status === "active" && (
          <div className="absolute bottom-2 right-2">
            <Button onClick={stopPreview} variant="secondary" size="sm">
              Stop Preview
            </Button>
          </div>
        )}

        {/* Name overlay when active */}
        {status === "active" && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-white text-sm">{userName} (Preview)</span>
            </div>
          </div>
        )}
      </div>

      {/* Device selectors and microphone level - shown when active */}
      {status === "active" && (
        <div className="space-y-3">
          {/* Device selectors */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
	              {/* Camera selector */}
	              <div>
	                <label htmlFor="preview-camera" className="block text-xs text-muted-foreground mb-1">
	                  Camera
	                </label>
	                <select
	                  id="preview-camera"
	                  value={selectedVideoDevice}
	                  onChange={(e) => handleVideoDeviceChange(e.target.value)}
	                  className="w-full px-2 py-1.5 text-sm border rounded-md bg-background"
	                >
                  {videoDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                    </option>
                  ))}
                </select>
              </div>
	              {/* Microphone selector */}
	              <div>
	                <label htmlFor="preview-microphone" className="block text-xs text-muted-foreground mb-1">
	                  Microphone
	                </label>
	                <select
	                  id="preview-microphone"
	                  value={selectedAudioDevice}
	                  onChange={(e) => handleAudioDeviceChange(e.target.value)}
	                  className="w-full px-2 py-1.5 text-sm border rounded-md bg-background"
	                >
                  {audioDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Microphone level indicator */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" x2="12" y1="19" y2="22"/>
            </svg>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Microphone Level</span>
                <span className="text-xs text-muted-foreground">{audioLevel}%</span>
              </div>
              <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-75 rounded-full ${
                    audioLevel > 70 ? "bg-green-500" : audioLevel > 30 ? "bg-sage-500" : "bg-stone-500"
                  }`}
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
            </div>
            {audioLevel > 10 && (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface LocalVideoTileProps {
  participant: ReturnType<typeof useVideoRoom>["localParticipant"];
  name: string;
  isMuted: boolean;
  isCameraOff: boolean;
  pip?: boolean;
}

function LocalVideoTile({ participant, name, isMuted, isCameraOff, pip }: LocalVideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [, setHasVideo] = useState(false);

  useEffect(() => {
    if (!participant) return;

    const attachTrack = () => {
      const videoTrack = participant.getTrackPublication(Track.Source.Camera);
      if (videoTrack?.track && videoRef.current) {
        videoTrack.track.attach(videoRef.current);
        setHasVideo(true);
      }
    };

    // Try attaching immediately
    attachTrack();

    // Also listen for track publications (tracks may arrive after connection)
    const onTrackPublished = () => attachTrack();
    participant.on("trackPublished", onTrackPublished);
    participant.on("localTrackPublished", onTrackPublished);

    return () => {
      participant.off("trackPublished", onTrackPublished);
      participant.off("localTrackPublished", onTrackPublished);
      const videoTrack = participant.getTrackPublication(Track.Source.Camera);
      videoTrack?.track?.detach();
    };
  }, [participant]);

  if (pip) {
    return (
      <>
        {isCameraOff ? (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-sage-600 flex items-center justify-center mx-auto mb-2">
                <span className="text-lg font-semibold text-white">
                  {name.charAt(0).toUpperCase()}
                </span>
              </div>
              <p className="text-white text-sm font-medium">{name}</p>
            </div>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent">
          <span className="text-white text-xs">Sie</span>
        </div>
      </>
    );
  }

  return (
    <div className="relative bg-stone-900 rounded-xl overflow-hidden">
      {isCameraOff ? (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-sage-600 flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl font-semibold text-white">
                {name.charAt(0).toUpperCase()}
              </span>
            </div>
            <p className="text-white font-medium">{name}</p>
            <p className="text-stone-400 text-sm">Camera off</p>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-medium">{name} (You)</span>
          {isMuted && <span className="text-red-400 text-xs">Muted</span>}
        </div>
      </div>
    </div>
  );
}

interface RemoteVideoTileProps {
  participant: ReturnType<typeof useVideoRoom>["remoteParticipants"][0];
  spotlight?: boolean;
}

function RemoteVideoTile({ participant, spotlight }: RemoteVideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCameraOff, setIsCameraOff] = useState(true);

  useEffect(() => {
    if (!participant) return;

    const attachTrack = () => {
      const videoTrack = participant.getTrackPublication(Track.Source.Camera);
      if (videoTrack?.track && videoRef.current) {
        videoTrack.track.attach(videoRef.current);
        setIsCameraOff(false);
      } else {
        setIsCameraOff(true);
      }
    };

    // Try attaching immediately (in case tracks already exist)
    attachTrack();

    // Listen for track events
    const onTrackSubscribed = (_track: any, publication: any) => {
      if (publication?.source === Track.Source.Camera) {
        attachTrack();
      }
    };

    const onTrackPublished = () => {
      attachTrack();
    };

    participant.on("trackSubscribed", onTrackSubscribed);
    participant.on("trackPublished", onTrackPublished);

    return () => {
      participant.off("trackSubscribed", onTrackSubscribed);
      participant.off("trackPublished", onTrackPublished);
      const videoTrack = participant.getTrackPublication(Track.Source.Camera);
      videoTrack?.track?.detach();
    };
  }, [participant]);

  const getName = () => {
    try {
      const metadata = JSON.parse(participant.metadata || "{}");
      return metadata.name || participant.identity;
    } catch {
      return participant.identity;
    }
  };

  return (
    <div className={spotlight ? "absolute inset-0" : "relative bg-stone-900 rounded-xl overflow-hidden"}>
      {/* Always render video element so ref is available for track attachment */}
      {/* Live video has no captions track; captions are not available in this debug view. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className={`absolute inset-0 w-full h-full object-cover ${isCameraOff ? "hidden" : ""}`}
      />
      {/* Avatar overlay when camera is off */}
      {isCameraOff && (
        <div className="absolute inset-0 flex items-center justify-center bg-stone-800">
          <div className="text-center">
            <div className={`${spotlight ? "w-32 h-32" : "w-20 h-20"} rounded-full bg-sage-600 flex items-center justify-center mx-auto ${spotlight ? "mb-4" : "mb-3"}`}>
              <span className={`${spotlight ? "text-5xl" : "text-3xl"} font-semibold text-white`}>
                {getName().charAt(0).toUpperCase()}
              </span>
            </div>
            <p className={`text-white font-medium ${spotlight ? "text-xl" : ""}`}>{getName()}</p>
            {!spotlight && <p className="text-stone-400 text-sm">Camera off</p>}
            {spotlight && <p className="text-stone-400 text-sm mt-1">Versicherter</p>}
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
        <span className="text-white text-sm font-medium">{getName()}</span>
      </div>
    </div>
  );
}
